import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import { Users, Zap, Play, Clock, ArrowRight, BarChart3, Send } from 'lucide-react';
import { Panel } from './BuyerWidgets';
import { OfferGoalRing } from './OfferGoalRing';

export const STAGE_COLORS = {
  New: '#3B82F6', 'Offer Sent': '#8B5CF6', 'Follow-Up': '#06B6D4', 'Price Watch': '#EC4899', 'Rejected Countered': '#F97316', 'Rejected NC': '#F43F5E', 'Accepted Other Offer': '#64748B', 'Under Contract': '#F59E0B',
  Marketed: '#EC4899', Sold: '#10B981', Closed: '#059669', Dead: '#64748B', Cancelled: '#475569',
};

export function KpiCard({ label, value, sub, icon: Icon, color, testId, valueTestId, delay = 0 }) {
  return (
    <div data-testid={testId} style={{ animationDelay: `${delay}ms` }}
      className="animate-fade-in-up group relative overflow-hidden rounded-2xl border border-[#1F293D] bg-[#111827] p-5 opacity-0 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#334155]">
      <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-30 blur-2xl transition-opacity group-hover:opacity-50" style={{ background: color }} />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
          <p className="mt-2 text-3xl font-bold tracking-tight text-white tabular-nums lg:text-4xl" data-testid={valueTestId}>{value}</p>
          <p className="mt-1 text-xs text-slate-500">{sub}</p>
        </div>
        <span className="hidden h-11 w-11 shrink-0 place-items-center rounded-xl sm:grid" style={{ background: `${color}1f`, boxShadow: `inset 0 0 0 1px ${color}40` }}>
          <Icon className="h-5 w-5" style={{ color }} strokeWidth={1.75} />
        </span>
      </div>
    </div>
  );
}

export function PipelineWidget({ pipeline }) {
  const data = Object.entries(pipeline || {}).map(([name, value]) => ({ name, value }));
  return (
    <Panel title="Pipeline Overview" icon={BarChart3} accent="#3B82F6" testId="widget-pipeline" className="lg:col-span-8">
      {data.some(d => d.value) ? (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={data} barSize={28}>
            <CartesianGrid stroke="#1E293B" vertical={false} />
            <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} interval={typeof window !== 'undefined' && window.innerWidth < 640 ? 1 : 0} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
            <Tooltip cursor={{ fill: 'rgba(59,130,246,0.06)' }} contentStyle={{ background: 'rgba(11,15,23,0.95)', border: '1px solid #334155', borderRadius: 10, fontSize: 12, color: '#F1F5F9' }} labelStyle={{ color: '#F8FAFC' }} />
            <Bar dataKey="value" radius={[6, 6, 0, 0]}>
              {data.map(e => <Cell key={e.name} fill={STAGE_COLORS[e.name] || '#3B82F6'} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : <div className="grid h-[250px] place-items-center text-sm text-slate-500">No pipeline data yet</div>}
    </Panel>
  );
}

export function RecentLeadsWidget({ leads }) {
  const navigate = useNavigate();
  return (
    <Panel title="Recent Leads" icon={Users} accent="#06B6D4" testId="widget-recent-leads" className="lg:col-span-4"
      action={<button onClick={() => navigate('/leads')} className="flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200" data-testid="recent-leads-view-all">View all <ArrowRight className="h-3 w-3" /></button>}>
      {leads?.length ? (
        <div className="space-y-1.5">
          {leads.map(l => (
            <button key={l.id} data-testid={`recent-lead-${l.id}`} onClick={() => navigate(`/leads/${l.id}`)}
              className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-[#1A2436]">
              <span className="h-8 w-1 rounded-full" style={{ background: STAGE_COLORS[l.status] || '#64748B' }} />
              <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-white">{l.name}</span><span className="block truncate text-[11px] text-slate-500">{l.property_address || 'No address'}</span></span>
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide" style={{ color: STAGE_COLORS[l.status] || '#94A3B8' }}>{l.status}</span>
            </button>
          ))}
        </div>
      ) : <p className="py-10 text-center text-sm text-slate-500">No leads yet</p>}
    </Panel>
  );
}

function timeUntil(iso) {
  const diff = new Date(iso) - new Date();
  if (diff < 0) return 'Overdue';
  const h = Math.floor(diff / 3.6e6), m = Math.floor((diff % 3.6e6) / 6e4);
  return h > 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function DripWidget({ drip = {} }) {
  const navigate = useNavigate();
  const tiles = [
    ['Active', drip.active_campaigns, '#F8FAFC', 'drip-active-campaigns'], ['Running', drip.active_assignments, '#60A5FA', 'drip-running'],
    ['Paused', drip.paused_assignments, '#FBBF24', 'drip-paused'], ['Done', drip.completed_assignments, '#34D399', 'drip-completed'], ['Auto emails', drip.emails_sent, '#C084FC', 'drip-emails-auto'],
  ];
  return (
    <Panel title="Drip Campaigns" icon={Zap} accent="#F59E0B" testId="drip-dashboard-section" className="lg:col-span-6"
      action={<button onClick={() => navigate('/drip-campaigns')} className="flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200" data-testid="drip-manage-btn">Manage <ArrowRight className="h-3 w-3" /></button>}>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {tiles.map(([label, v, color, tid]) => (
          <div key={label} className="rounded-xl bg-[#161F30] p-3 text-center">
            <p className="text-2xl font-bold tabular-nums" style={{ color }} data-testid={tid}>{v || 0}</p>
            <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 space-y-1.5">
        {(drip.upcoming_actions || []).length === 0 && <p className="text-center text-xs text-slate-500">No upcoming drip actions</p>}
        {(drip.upcoming_actions || []).map(a => (
          <div key={a.id} data-testid={`upcoming-action-${a.id}`} className="flex items-center gap-3 rounded-xl bg-[#0F1623] px-3 py-2">
            <Play className="h-3.5 w-3.5 text-blue-400" />
            <span className="min-w-0 flex-1"><span className="block truncate text-sm text-white">{a.campaign_name}</span><span className="text-[11px] text-slate-500">{a.target_name || 'Unknown'} · Step {(a.current_step_index || 0) + 1}/{a.total_steps}</span></span>
            <span className="flex items-center gap-1 text-xs text-blue-300"><Clock className="h-3 w-3" />{timeUntil(a.next_action_at)}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

const OFFER_COLORS = ['#22D3EE', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B'];

export function OffersSentWidget({ data, onGoalSaved }) {
  const periods = data?.periods || [];
  const max = Math.max(1, ...periods.map(p => p.count));
  const money = v => (v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${Math.round(v / 1e3)}k` : `$${Math.round(v)}`);
  return (
    <Panel title="Offers Sent" icon={Send} accent="#22D3EE" testId="widget-offers-sent" className="lg:col-span-12">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <OfferGoalRing week={data?.week} onSaved={onGoalSaved} />
        {periods.map((p, i) => (
          <div key={p.key} className="relative overflow-hidden rounded-xl border border-[#1F293D] bg-[#0F1623] p-4 last:max-sm:col-span-2" data-testid={`offers-${p.key}`}>
            <div className="absolute inset-x-0 bottom-0 h-1 bg-[#1E293B]"><div className="h-full transition-[width] duration-700" style={{ width: `${(p.count / max) * 100}%`, background: OFFER_COLORS[i] }} /></div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{p.label}</p>
            <p className="mt-1.5 text-3xl font-bold tabular-nums" style={{ color: OFFER_COLORS[i] }} data-testid={`offers-${p.key}-count`}>{p.count}</p>
            <p className="text-xs text-slate-400 tabular-nums" data-testid={`offers-${p.key}-value`}>{p.value ? `${money(p.value)} in offers` : 'no offer value'}</p>
          </div>
        ))}
      </div>
    </Panel>
  );
}
